/**
 * THE cents-to-euro formatter. One function, every surface.
 *
 * T549 M3 (consistency audit A3, docs/research/ecmb-email-channel-research.md
 * section 8): the same booking's deposit rendered as "25 euro" on Helen's screen
 * and "24.60 euro" in the customer's SMS, because four independent formatters
 * had grown up side by side and each answered the fractional case differently.
 * One of them (engine/money.ts formatEUR) FLOORED, so 9.99 became 9. Every
 * formatter now delegates here.
 *
 * THE CONTRACT (this comment is the whole specification):
 *   whole euros -> the euro sign + the grouped integer         125000 -> 1,250
 *   fractional  -> the euro sign + grouped, exactly 2 decimals   2460 -> 24.60
 *   never floors; never forces trailing zeros on a whole amount.
 *
 * Grouping is RETAINED (en-IE) rather than dropped, because every existing
 * whole-euro assertion in the repo depends on it (test/engine/money.spec.ts
 * pins 1,250 and 3,000). That makes the reconciliation non-breaking on the whole
 * amounts everything is pinned at, and changes only the fractional case, which
 * is the actual defect. Adding grouping DOES change the operator/driver messages
 * at 1000 and above (they used to render ungrouped); that is deliberate - it is
 * the same concept rendered two ways, which is what this file exists to stop.
 *
 * DISPLAY ONLY. `core/deposit-policy.ts computeDepositCents` remains the sole
 * source of the amount actually charged; nothing here may change what a customer
 * pays, only how that number is written down.
 */
export function formatEuro(cents: number): string {
  // The whole/fractional decision is taken on the INTEGER cents, never on the
  // divided value: (2460 / 100) is 24.599999... in binary floating point, so
  // `Number.isInteger(cents / 100)` would be deciding money on a float. A
  // non-integer cents value (nothing in the system writes one) falls into the
  // fractional branch, which is the honest side to err on.
  const whole = cents % 100 === 0;
  return '€' + (cents / 100).toLocaleString('en-IE', {
    minimumFractionDigits: whole ? 0 : 2,
    maximumFractionDigits: whole ? 0 : 2,
  });
}
